import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import AccountAdminSettings from '@/app/components/account/account-admin-settings.tsx';
import NoAccountBanner from '@/app/components/account/no-account-banner.tsx';
import { PageLoading } from '@/app/components/page-loading.tsx';
import { useAuth } from '@/app/hooks/auth-provider';
import { useRouteAccount } from '@/app/hooks/use-route-account.hook.ts';
import toast from '@/app/toast.ts';
import { usePutAccountMutation } from '@/slices/account-api-slice.ts';
import type { AccountModel } from '../../../types/account.type.ts';
import { type StaffMemberModel, StaffRoleEnum } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';
import AccountFormFields from './account-form-fields.tsx';

export default function EditAccount() {
  const { user } = useAuth();
  const { account, isLoading } = useRouteAccount({ basePath: '/account/edit' });
  const navigate = useNavigate();

  const form = useForm<AccountModel>({
    values: {
      ...account,
      specialties: account?.specialties ?? [],
    },
    criteriaMode: 'all',
    resetOptions: {
      keepErrors: false,
    },
  });

  const { handleSubmit } = form;
  const [persist, { isLoading: isUpdating }] = usePutAccountMutation();

  const onSubmit: SubmitHandler<AccountModel> = (value: AccountModel) => {
    const newValue = {
      ...value,
    };
    persist(newValue)
      .unwrap()
      .then(() => {
        toast.success('Account Saved!');
        navigate(-1);
      })
      .catch(() => {
        toast.error('Error saving account');
      });
  };

  if (isLoading) {
    return <PageLoading />;
  }

  if (!account) {
    return (
      <div className='flex flex-col gap-4'>
        <h2 className='text-xl'>Update Account</h2>
        <NoAccountBanner />
      </div>
    );
  }

  const renderView = () => {
    switch (user?.userType) {
      case UserTypeEnum.Admin:
        return <AccountFormFields account={account} />;
      case UserTypeEnum.StaffMember: {
        const staff = user as StaffMemberModel;
        if (
          staff.accountSiteStaffMembers.some(
            x => x.accountId === account?.id && x.staffRole.id >= StaffRoleEnum.SiteAdministrator,
          )
        ) {
          return <AccountFormFields account={account} />;
        }
        break;
      }
      default:
        return <Navigate to={'/dashboard'} />;
    }
  };

  return (
    <FormProvider {...form}>
      <div className='flex items-center gap-2 mb-4'>
        <Button isIconOnly variant='flat' color='primary' onPress={() => navigate(-1)}>
          <ArrowLeftIcon className='size-4' />
        </Button>
        <h5 className='text-primary-600'>Update Account</h5>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
        {renderView()}
        <div className='max-w-screen-lg self-center w-full'>
          <Button size='lg' isLoading={isUpdating} color='primary' variant='solid' type='submit'>
            Save Account
          </Button>
        </div>
        {user?.userType === UserTypeEnum.Admin && <AccountAdminSettings account={account} />}
      </form>
    </FormProvider>
  );
}
